/** /blacklists — delivery blacklist rules.
 *
 *  The row click opens the editor, so every value that names something else is
 *  a hover trigger (`blacklistLinks`) rather than a link: tenant, blocked user,
 *  OTA, the matched pattern, the condition's tag chips, the event codes, the
 *  redirect inboxes and the two actor columns. The popover's own click is
 *  swallowed, so opening actions never also opens the dialog.
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { blacklistsApi, type BlacklistRuleRow } from '@/api/blacklists';
import { ConsoleApiError } from '@/api/client';
import { deleteBatchAction } from '@/components/DataGrid/batchTypes';
import {
  ResourceDataGrid, type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { chipSummaryLabels, fromWireChips } from '@/components/TagConditionInput';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { pageTenantsFrom } from '@/scope/pageTenants';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

import {
  actorActions,
  redirectEmailActions,
  ruleEventActions,
  ruleOtaActions,
  rulePatternActions,
  ruleTagChipActions,
  ruleTenantActions,
  ruleUserActions,
} from './blacklistLinks';
import { BlacklistRuleDialog } from './BlacklistRuleDialog';

/** Per-chip display labels of a match_type=tag rule ("wan · VIP · gold"). */
function tagChipLabels(row: BlacklistRuleRow): string[] {
  if (row.match_type !== 'tag') return [];
  return chipSummaryLabels(fromWireChips(row.tags, row.tag_match_mode));
}

/** A comma-separated inbox list, each address its own trigger. */
function EmailList({ value, row }: { value: string | null; row: BlacklistRuleRow }) {
  const addresses = (value ?? '').split(',').map((a) => a.trim()).filter(Boolean);
  if (addresses.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ overflow: 'hidden' }}>
      {addresses.map((a, i) => (
        <Box component="span" key={a}>
          {i > 0 && ', '}
          <ValueActions actions={redirectEmailActions(a, ctxOf(row))}>{a}</ValueActions>
        </Box>
      ))}
    </Stack>
  );
}

/** Target-column text for a tag rule: "3 tag(s) AND". */
function tagTargetLabel(row: BlacklistRuleRow): string {
  const n = tagChipLabels(row).length;
  return `${n} tag${n === 1 ? '' : 's'} ${(row.tag_match_mode ?? 'and').toUpperCase()}`;
}

/** Every grid link is scoped to the RULE's operator, not the toolbar filter —
 *  a system rule stays unscoped. */
const ctxOf = (row: BlacklistRuleRow) => ({ tenantId: row.tour_operator?.id ?? null });

export function BlacklistsGrid() {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_blacklist_manage');

  // Operators (several, D1) and the Scope select's "System only" are exclusive, like
  // a sentinel (D3): picking operators returns the select to All, and it reads All,
  // disabled, while any are picked (the console scope's included).
  const [scope, setScope] = useState<'all' | 'system'>('all');
  const [tops, setTopsState] = useScopedTenantsState();
  const setTops = (next: typeof tops) => {
    setTopsState(next);
    if (next.length > 0) setScope('all');
  };
  const topIds = formatTenantIds(tops.map((t) => t.id));
  const [deliveryType, setDeliveryType] = useState<string>('');
  const [matchType, setMatchType] = useState<string>('');
  const [isActive, setIsActive] = useState<string>('');
  const [editId, setEditId] = useState<number | null | 'new'>(null);
  useCreateIntent(() => setEditId('new'));
  const [deleteRow, setDeleteRow] = useState<BlacklistRuleRow | null>(null);
  const [deleteReason, setDeleteReason] = useState('');

  // The create dialog's page tenants (D9): the grid's operator filter.
  const pageTenants = pageTenantsFrom(topIds, tops);

  const tourOperatorParam: string | undefined =
    topIds ?? (scope === 'system' ? 'system' : undefined);

  // Keyed by the ACTUAL API param names so ResourceDataGrid can derive the
  // batch `filterQuery` from `extra` (the queryFn below reads the same values
  // from closure, so it's unaffected by the key names).
  const extra = useMemo(
    () => ({
      tour_operator: String(tourOperatorParam ?? ''),
      delivery_type: deliveryType,
      match_type: matchType,
      is_active: isActive,
    }),
    [tourOperatorParam, deliveryType, matchType, isActive],
  );

  function queryFn(params: ResourceDataGridParams) {
    return blacklistsApi.list({
      page: params.page,
      page_size: params.pageSize,
      ordering: params.ordering,
      search: params.search,
      tour_operator: tourOperatorParam,
      delivery_type: deliveryType || undefined,
      match_type: matchType || undefined,
      is_active: isActive || undefined,
    });
  }

  const refresh = () => qc.invalidateQueries({ queryKey: ['consoleBlacklistRules'] });

  const doDelete = () => {
    if (!deleteRow || !deleteReason.trim()) return;
    blacklistsApi
      .remove(deleteRow.id, deleteReason.trim())
      .then(() => {
        snackbar.show('Rule deleted', 'success');
        setDeleteRow(null);
        setDeleteReason('');
        refresh();
      })
      .catch((e: unknown) =>
        snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed', 'error'),
      );
  };

  const columns = useMemo<GridColDef<BlacklistRuleRow>[]>(
    () => [
      {
        field: 'scope', headerName: 'Scope', width: 90, sortable: false, filterable: false,
        renderCell: (p) => <Chip size="small" label={p.row.scope === 'system' ? 'System' : 'Tenant'} />,
      },
      {
        field: 'tour_operator', headerName: 'Tenant', flex: 0.9, sortable: false, filterable: false,
        valueGetter: (_v, row) => (row.tour_operator ? `${row.tour_operator.name} (${row.tour_operator.code})` : '—'),
        renderCell: (p) =>
          p.row.tour_operator ? (
            <WithTenantMark environment={p.row.tour_operator.environment}>
              <ValueActions actions={ruleTenantActions(p.row.tour_operator)}>{p.value}</ValueActions>
            </WithTenantMark>
          ) : (
            '—'
          ),
      },
      { field: 'delivery_type', headerName: 'Delivery', width: 100, filterable: false,
        valueGetter: (_v, row) => row.delivery_type_display },
      { field: 'match_type', headerName: 'Match', width: 120, filterable: false,
        valueGetter: (_v, row) => row.match_type_display },
      {
        field: 'pattern', headerName: 'Target', flex: 1.2, sortable: false, filterable: false,
        valueGetter: (_v, row) =>
          row.match_type === 'user'
            ? row.user ? `user: ${row.user.email ?? row.user.id}` : 'user'
            : row.match_type === 'ota'
              ? row.ota ? `OTA: ${row.ota.name}` : 'OTA'
              : row.match_type === 'tag'
                ? tagTargetLabel(row)
                : row.pattern ?? '',
        renderCell: (p) => {
          const row = p.row;
          const actions =
            row.match_type === 'user'
              ? row.user ? ruleUserActions(row.user, ctxOf(row)) : []
              : row.match_type === 'ota'
                ? row.ota ? ruleOtaActions(row.ota) : []
                : row.match_type === 'tag' || !row.pattern
                  ? []
                  : rulePatternActions(row.match_type, row.pattern, ctxOf(row));
          return actions.length > 0 ? (
            <ValueActions actions={actions}>{p.value}</ValueActions>
          ) : (
            p.value
          );
        },
      },
      // Not in the backend ordering whitelist — keep sortable: false.
      {
        field: 'tags', headerName: 'Tags', flex: 1.2, sortable: false, filterable: false,
        valueGetter: (_v, row) => tagChipLabels(row).join(', '),
        renderCell: (p) => {
          const labels = tagChipLabels(p.row);
          if (labels.length === 0) return null;
          const [first = '', second] = labels;
          const extra = labels.length - (second === undefined ? 1 : 2);
          // `chipSummaryLabels` maps the row's chips 1:1, so label i belongs to
          // entry i — the entry is what carries the tag and category ids.
          const entries = p.row.tags ?? [];
          const chip = (label: string, i: number) => {
            const entry = entries[i];
            const node = <Chip size="small" variant="outlined" label={label} />;
            return entry ? (
              <ValueActions chip key={i} actions={ruleTagChipActions(entry, ctxOf(p.row))}>
                {node}
              </ValueActions>
            ) : (
              node
            );
          };
          return (
            <Tooltip title={`${labels.join(' · ')} — sources: ${(p.row.tag_sources ?? []).join(', ')}`}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: 'hidden' }}>
                {chip(first, 0)}
                {second !== undefined && chip(second, 1)}
                {extra > 0 && <Typography variant="caption" color="text.secondary">+{extra}</Typography>}
              </Stack>
            </Tooltip>
          );
        },
      },
      {
        field: 'events', headerName: 'Events', flex: 0.8, sortable: false, filterable: false,
        renderCell: (p) => {
          if (p.row.block_all_events) return <Chip size="small" label="All events" />;
          const label = `${p.row.event_codes.length + p.row.event_groups.length} selected`;
          // Groups are not offered: nothing takes a group id in a URL.
          const actions = ruleEventActions(p.row.event_codes, ctxOf(p.row));
          return actions.length > 0 ? (
            <ValueActions actions={actions}>{label}</ValueActions>
          ) : (
            label
          );
        },
      },
      { field: 'is_active', headerName: 'Active', type: 'boolean', width: 80, filterable: false },
      // Redirect fields are not in the backend ordering whitelist — keep sortable: false.
      { field: 'redirect_to_email', headerName: 'Redirect to', flex: 1, sortable: false, filterable: false,
        valueGetter: (_v, row) => row.redirect_to_email ?? '',
        // One rule can redirect to several inboxes; each is its own trigger.
        renderCell: (p) => <EmailList value={p.row.redirect_to_email} row={p.row} /> },
      { field: 'redirect_reply_to_email', headerName: 'Reply-To', flex: 0.8, sortable: false, filterable: false,
        valueGetter: (_v, row) => row.redirect_reply_to_email ?? '',
        renderCell: (p) => <EmailList value={p.row.redirect_reply_to_email} row={p.row} /> },
      { field: 'comment', headerName: 'Comment', flex: 1, sortable: false, filterable: false },
      { field: 'updated_ts', headerName: 'Updated', width: 160, filterable: false,
        valueGetter: (_v, row) => (row.updated_ts ? row.updated_ts.replace('T', ' ').slice(0, 16) : '') },
      // created_ts is in the backend ordering_fields, so header sort round-trips.
      { field: 'created_ts', headerName: 'Created', width: 160, filterable: false,
        valueGetter: (_v, row) => (row.created_ts ? row.created_ts.replace('T', ' ').slice(0, 16) : '') },
      { field: 'exceptions', headerName: 'Exceptions', width: 120, sortable: false, filterable: false,
        valueGetter: (_v, row) => row.exception_event_codes.length + row.exception_event_groups.length,
        renderCell: (p) => {
          const n = p.row.exception_event_codes.length + p.row.exception_event_groups.length;
          if (n === 0) return null;
          const chip = <Chip size="small" variant="outlined" label={`${n} exception${n === 1 ? '' : 's'}`} />;
          const actions = ruleEventActions(p.row.exception_event_codes, ctxOf(p.row));
          return actions.length > 0 ? (
            <ValueActions chip actions={actions}>{chip}</ValueActions>
          ) : (
            chip
          );
        } },
      { field: 'created_by', headerName: 'Created by', flex: 0.8, sortable: false, filterable: false,
        valueGetter: (_v, row) => row.created_by?.email ?? '—',
        renderCell: (p) =>
          p.row.created_by ? (
            <ValueActions actions={actorActions(p.row.created_by)}>{p.value}</ValueActions>
          ) : ('—') },
      { field: 'updated_by', headerName: 'Updated by', flex: 0.8, sortable: false, filterable: false,
        valueGetter: (_v, row) => row.updated_by?.email ?? '—',
        renderCell: (p) =>
          p.row.updated_by ? (
            <ValueActions actions={actorActions(p.row.updated_by)}>{p.value}</ValueActions>
          ) : ('—') },
      {
        field: 'actions', headerName: '', width: 90, sortable: false, filterable: false,
        renderCell: (p) => (
          <Stack direction="row">
            <Tooltip title="Edit">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditId(p.row.id); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canManage && (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteRow(p.row); setDeleteReason(''); }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [canManage],
  );

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <div style={{ minWidth: 300 }}>
          <TenantPicker multiple fieldWidth={300} label="Tour operator" placeholder="All tenants…"
            value={tops} onChange={setTops} />
        </div>
        <TextField size="small" select label="Scope" sx={{ width: 140 }}
          value={tops.length > 0 ? 'all' : scope} disabled={tops.length > 0}
          onChange={(e) => setScope(e.target.value as 'all' | 'system')}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="system">System only</MenuItem>
        </TextField>
        <TextField size="small" select label="Delivery type" value={deliveryType} sx={{ width: 150 }}
          onChange={(e) => setDeliveryType(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="sms">SMS</MenuItem>
          <MenuItem value="whatsapp">WhatsApp</MenuItem>
          <MenuItem value="airbnb">Airbnb</MenuItem>
        </TextField>
        <TextField size="small" select label="Match type" value={matchType} sx={{ width: 160 }}
          onChange={(e) => setMatchType(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="exact">Exact match</MenuItem>
          <MenuItem value="substring">Substring match</MenuItem>
          <MenuItem value="regex">Regular expression</MenuItem>
          <MenuItem value="user">User</MenuItem>
          <MenuItem value="ota">OTA</MenuItem>
          <MenuItem value="reseller">Reseller</MenuItem>
        </TextField>
        <TextField size="small" select label="Active" value={isActive} sx={{ width: 120 }}
          onChange={(e) => setIsActive(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Active</MenuItem>
          <MenuItem value="false">Inactive</MenuItem>
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <PermissionGate perm="console_blacklist_manage">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditId('new')}>New rule</Button>
        </PermissionGate>
      </Stack>

      <ResourceDataGrid<BlacklistRuleRow>
        queryKey={['consoleBlacklistRules', extra]}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        batchResource="blacklist_rules"
        batchActions={[deleteBatchAction('console_blacklist_manage', { reason: 'required' })]}
        defaultSort={{ field: 'updated_ts', sort: 'desc' }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              redirect_reply_to_email: false,
              created_ts: false,
              exceptions: false,
              created_by: false,
              updated_by: false,
            },
          },
        }}
        onRowClick={(id) => setEditId(id as number)}
        headerToolbar={{ search: true, searchPlaceholder: 'Search pattern / comment', exportFileName: 'blacklist-rules' }}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
      />

      {editId !== null && (
        <BlacklistRuleDialog
          ruleId={editId === 'new' ? null : editId}
          pageTenants={pageTenants}
          onClose={() => setEditId(null)}
          onSaved={() => { setEditId(null); refresh(); }}
        />
      )}

      <Dialog open={deleteRow !== null} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete blacklist rule</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Deliveries matched by this rule will no longer be blocked. State why.
          </DialogContentText>
          <TextField autoFocus fullWidth required label="Reason" value={deleteReason}
            error={!deleteReason.trim()} onChange={(e) => setDeleteReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!deleteReason.trim()} onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
